<template>
  <div class="skill-replace-wrapper">
    <!-- 顶部宝可梦卡片横排 -->
    <div class="pokemon-scroll-row">
      <div
        v-for="(pokemon, index) in formattedPokemons"
        :key="index"
        class="pokemon-card"
        :class="{ selected: selectedIndex === index }"
        @click="selectPokemon(index)"
      >
        <div class="pokemon-icon-wrapper">
          <img v-if="pokemon" :src="pokemon.icon_image" class="pokemon-icon" />
          <span v-else class="empty">空</span>
        </div>
      </div>
    </div>

    <!-- 技能内容区域 -->
    <div class="main-section">
      <!-- 当前技能 -->
      <div class="skill-column current-skills">
        <h3>当前技能</h3>
        <div class="skill-scroll bordered">
          <div
            v-for="(move, index) in currentPokemonMoves"
            :key="index"
            class="skill-item rectangle"
            :class="{ selected: selectedSkillIndex === index }"
            @click="selectSkillSlot(index)"
          >
            <p>{{ move.display_name || '空技能' }}</p>
            <small v-if="move.current_pp">PP: {{ move.current_pp }}/{{ move.max_pp }}</small>
          </div>
        </div>
      </div>

      <!-- 可学习技能 -->
      <div class="skill-column learnable-skills">
        <h3>可学习技能</h3>
        <div class="skill-scroll bordered">
          <template v-if="filteredLearnableSkills.length">
            <div
              v-for="(skill, index) in filteredLearnableSkills"
              :key="skill.id"
              class="skill-item rectangle"
              :class="{ selected: selectedLearnableSkillIndex === index }"
              @click="selectLearnableSkill(index)"
            >
              <p>{{ skill.display_name }}</p>
              <small>{{ skill.effect_description }}</small>
            </div>
          </template>
          <div v-else class="no-skill">无可学习技能</div>
        </div>
      </div>
    </div>

    <!-- 替换按钮 -->
    <div class="bottom-section">
      <button @click="replaceSkill" :disabled="!canReplaceSkill">确认替换</button>
    </div>

    <!-- 提示弹窗 -->
    <div v-if="showTip" class="tip-popup">技能替换成功！</div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';

const { proxy } = getCurrentInstance();
const userId = proxy.$getUserId();

const team = ref([]);
const selectedIndex = ref(0);
const selectedSkillIndex = ref(null);
const selectedLearnableSkillIndex = ref(null);
const currentPokemonMoves = ref([]);
const learnableSkills = ref([]);
const showTip = ref(false);

const formattedPokemons = computed(() => {
  const max = 6;
  return [...team.value, ...Array(max - team.value.length).fill(null)];
});



const filteredLearnableSkills = computed(() => {
  const learned = currentPokemonMoves.value.map((m) => m.display_name);
  return learnableSkills.value.filter(
    (m) => m && m.id && !learned.includes(m.display_name)
  );
});

const canReplaceSkill = computed(() =>
  selectedSkillIndex.value !== null && selectedLearnableSkillIndex.value !== null
);

const fetchTeam = async () => {
  const res = await proxy.$axios.get(`/pokemon/team/${userId}`);
  team.value = res.data;
  updateCurrentPokemonMoves();
};

const fetchLearnableSkills = async (pokemonId, level) => {
  const res = await proxy.$axios.get(`/pokemon/learnableMoves?pokemonId=${pokemonId}&level=${level}`);
  learnableSkills.value = res.data.learnableMoves;
};

function selectPokemon(index) {
  selectedIndex.value = index;
  selectedSkillIndex.value = null;
  selectedLearnableSkillIndex.value = null;
  updateCurrentPokemonMoves();
}

function updateCurrentPokemonMoves() {
  const p = team.value[selectedIndex.value];
  if (!p) return;
  currentPokemonMoves.value = [
    { display_name: p.move_1, current_pp: p.move_1_pp, max_pp: p.move_1_max_pp },
    { display_name: p.move_2, current_pp: p.move_2_pp, max_pp: p.move_2_max_pp },
    { display_name: p.move_3, current_pp: p.move_3_pp, max_pp: p.move_3_max_pp },
    { display_name: p.move_4, current_pp: p.move_4_pp, max_pp: p.move_4_max_pp }
  ];
  fetchLearnableSkills(p.pokemon_id, p.level);
}

function selectSkillSlot(index) {
  selectedSkillIndex.value = index;
}

function selectLearnableSkill(index) {
  selectedLearnableSkillIndex.value = index;
}

async function replaceSkill() {
  const move = filteredLearnableSkills.value[selectedLearnableSkillIndex.value];
  const slot = selectedSkillIndex.value;
  await proxy.$axios.post('/pokemon/replaceMove', {
    userId,
    position: selectedIndex.value + 1,
    newMove: move.internal_name,
    slot: slot + 1,
  });

  currentPokemonMoves.value[slot] = {
    display_name: move.display_name,
    current_pp: move.pp,
    max_pp: move.pp,
  };

  learnableSkills.value = learnableSkills.value.filter(
    (skill) => skill.internal_name !== move.internal_name
  );

  selectedSkillIndex.value = null;
  selectedLearnableSkillIndex.value = null;
  showTip.value = true;
  setTimeout(() => (showTip.value = false), 2000);
}

onMounted(fetchTeam);
</script>

<style scoped>
.skill-replace-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
  font-size: 90%;
  box-sizing: border-box;
}

.pokemon-scroll-row {
  display: flex;
  overflow-x: auto;
  padding: 10px;
  background: #ffffff;
  gap: 10px;
}

.pokemon-card {
  min-width: 60px;
  width: 60px;
  height: 60px;
  background: #ffffff;
  border: 2px solid #ccc;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pokemon-icon-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pokemon-icon {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

.pokemon-card.selected {
  border-color: #ff4444;
}

.main-section {
  flex: 1;
  display: flex;
  justify-content: space-between;
  padding: 10px;
  gap: 4%;
  height: 0;
  min-height: 0;
  box-sizing: border-box;
}

.skill-column {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 6px;
  padding: 8px;
  overflow: hidden;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.current-skills {
  width: 48%;
}

.learnable-skills {
  width: 48%;
}

.skill-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skill-item {
  background: #f0f0f0;
  padding: 8px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #ddd;
  text-align: center;
}

.skill-item.selected {
  background: #ffecec;
  border: 1px solid red;
}

.rectangle {
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.no-skill {
  text-align: center;
  color: #888;
  margin-top: 20px;
}

.bottom-section {
  padding: 10px;
  border-top: 1px solid #ddd;
  background: #fff;
}

.bottom-section button {
  width: 100%;
  background: #4caf50;
  color: white;
  font-size: 16px;
  border: none;
  border-radius: 8px;
  padding: 10px;
}

.tip-popup {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 999;
}
</style>